import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DialogButton } from '@beeinventor/dasiot-react-component-lib';
import {
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogProps,
  styled,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  Contact,
  DasIdWillAssign,
  EnvironmentPurpose,
  EnvironmentWillAssign,
  Group,
  StatusType,
} from '../../../types';
import { DasWBGT } from '../../../types/Device';
import { Environment } from '../../../types/Resource';

import { getProjectDasWBGT } from '../../../apis/DasWbgtApi';
import { updateEndPointFromProject } from '../../../apis/devices/CommonDevice';
import {
  bindProjectContact,
  unBindProjectContact,
} from '../../../apis/ProjectApi';
import {
  bindDeviceToResource,
  setResourceGroups,
  unbindDeviceFromResource,
  updateResource,
  uploadResourceAvatar,
} from '../../../apis/ResourceApi';
import { useAppSelector } from '../../../hooks';
import useFilteredGroups from '../../../hooks/useFilteredGroups';
import useHasPolicy from '../../../hooks/useHasPolicy';

import CheckListItem from '../../../components/CheckListItem';
import ConfirmButton from '../../../components/ConfirmButton';
import ManagementDialog from '../../../components/Dialog/ManagementDialog';

import CompleteButtonSvgIcon from '../../../assets/SvgIcon/CompleteButtonSvgIcon';
import LoadingButtonSvgIcon from '../../../assets/SvgIcon/LoadingButtonSvgIcon';
import SaveSvgIcon from '../../../assets/SvgIcon/SaveSvgIcon';
import mainTheme from '../../../theme';
import { checkIsArrayEqual } from '../../../utils/checkArrayEqual';
import {
  navigatorEnvironmant as navigator,
  parseDasId,
} from '../../../utils/common';
import ManagementNoPermissionContent from '../../ManagementNoPermissionContent';
import ConnectedDevice from '../edit-content/ConnectedDeviceContent';
import ContactEdit from '../edit-content/ContactEdit';
import { DasWBGTEndpointUpdate } from '../edit-content/DasWBGTConnectedDeviceCard';
import EnvironmentBasicInformationContent from '../edit-content/EnvironmentBasicInformationContent';
import GroupEdit from '../edit-content/GroupEdit';
import PurposeAreaContent from '../edit-content/PurposeAreaContent';
import {
  checkArea,
  checkContactAvailable,
  checkEnvironmentBasicInformationAvailable,
  checkIsFormEnvironmentEdited,
} from '../validation/environment-validation';
import {
  checkIsAvatarUpdated,
  checkIsDasIdEqual,
  hasAssignedDevice,
} from '../validation/management-validation';

const DialogContent = styled(MuiDialogContent)`
  padding: 10px 20px;
  display: flex;
  flex: 1;
`;

const ContentWrapper = styled('div')`
  width: 340px;
  border-radius: 4px;
  display: flex;
  flex: 1;

  & .loading {
    height: 100%;
  }
`;

const DialogActions = styled(MuiDialogActions)`
  display: flex;
  padding: 24px;
  height: 80px;
`;

const CheckList = styled('div')`
  display: flex;
  flex-direction: column;
  width: 280px;
  margin-top: 20px;
`;

interface EditEnvironmentDialogProps extends DialogProps {
  title: string;
  navigatorIndex: number;
  groups: Array<Group> | undefined;
  selectedGroups: Array<Group>;
  selectedResource: Environment | undefined;
  resourceContacts: Array<Contact>;
  onSuccessCreate: () => void;
  onSuccessBind?: () => void;
  onCloseDialog: () => void;
  onSelectNavigatorIndex: (index: number) => void;
}

const EditEnvironment: React.FC<EditEnvironmentDialogProps> = ({
  open,
  title,
  navigatorIndex,
  groups,
  selectedGroups,
  selectedResource,
  resourceContacts,
  onCloseDialog,
  onSuccessCreate,
  onSuccessBind,
  onSelectNavigatorIndex,
}) => {
  const projectId = useParams().projectId;
  const { policies, role: userProjectRole } = useAppSelector(
    (store) => store.projects,
  );
  const queryClient = useQueryClient();
  const { mutateAsync: mutateUpdateEnvironment } = useMutation({
    mutationFn: updateResource,
  });

  const { mutateAsync: mutateUploadResourceAvatar } = useMutation({
    mutationFn: uploadResourceAvatar,
  });

  const { mutateAsync: mutateSetResourceGroups } = useMutation({
    mutationFn: setResourceGroups,
  });

  const { mutateAsync: mutateUnbindContact } = useMutation({
    mutationFn: unBindProjectContact,
  });

  const { mutateAsync: mutateBindContact } = useMutation({
    mutationFn: bindProjectContact,
  });

  const { t } = useTranslation();
  const [checkedValue, setCheckedValue] = useState(
    new Array(navigator.length).fill(false),
  );
  const [saveButtonStatus, setSaveButtonStatus] = useState<
    'loading' | 'success'
  >();
  const [environmentDataWillAssign, setEnvironmentDataWillAssign] =
    useState<EnvironmentWillAssign>();
  const [groupsWillAssign, setGroupsWillAssign] = useState<Array<string>>([]);
  const [listContact, setListContact] = useState<string[]>(
    resourceContacts.map((contact) => contact.id),
  );
  const hasBindDevicePolicy = useHasPolicy(policies, 'WriteDeviceBinding');
  const [status, setStatus] = useState<StatusType>('default');
  const [message, setMessage] = useState('');
  const [checkedNavigator, setCheckedNavigator] = useState(
    new Array(navigator.length).fill(false),
  );
  const [deviceWillAssign, setDeviceWillAssign] = useState<
    Array<DasIdWillAssign>
  >([]);

  const [wbgtEndpointUpdates, setWbgtEndpointUpdates] = useState<
    Record<string, DasWBGTEndpointUpdate>
  >({});

  const [environmentPurpose, setEnvironmentPurpose] =
    useState<EnvironmentPurpose>();

  useEffect(() => {
    if (environmentPurpose !== 'heatstroke-system') {
      const connectedDeviceIndex = navigator.findIndex(
        (item) => item.name === 'connected-device',
      );
      if (navigatorIndex === connectedDeviceIndex) {
        onSelectNavigatorIndex(connectedDeviceIndex - 1);
      }
    }
  }, [environmentPurpose]);

  const { mutateAsync: mutateBindDeviceToResource } = useMutation({
    mutationFn: bindDeviceToResource,
  });
  const { mutateAsync: mutateUnbindDeviceToResource } = useMutation({
    mutationFn: unbindDeviceFromResource,
  });
  const [assignedDeviceFirstInit, setAssignedDeviceFirstInit] = useState(false);

  const [initDevices, setInitDevices] = useState<Array<DasIdWillAssign>>([]);

  const initContact = resourceContacts.map((contact) => contact.id);

  const filteredGroups = useFilteredGroups(
    'WriteOneEnvironmentGroup',
    groups ?? [],
    userProjectRole,
    policies,
  );

  const handleBindDevice = async (dasIds: DasIdWillAssign[], id: string) => {
    const requestBindDevice = dasIds.map(async (dasId) => {
      const { data } = await mutateBindDeviceToResource({
        projectId: projectId as string,
        dasId: dasId.name,
        resourceType: 'environment',
        data: {
          resourceId: id,
        },
      });

      return data;
    });

    const result = await Promise.all(requestBindDevice).catch((err) => {
      const error = err as Error;
      setMessage(error.message);
      setStatus('error');
    });

    return result;
  };

  const handleUnbindDevice = async (dasIds: DasIdWillAssign[], id: string) => {
    const requestUnbindDevice = dasIds.map(async (d) => {
      const { data } = await mutateUnbindDeviceToResource({
        dasId: d.name,
        projectId: projectId as string,
        resourceType: 'environment',
        data: {
          resourceId: id,
        },
      });
      return data;
    });

    await Promise.all(requestUnbindDevice).catch((err) => {
      const error = err as Error;
      setMessage(error.message);
      setStatus('error');
    });
  };

  useEffect(() => {
    if (!selectedResource) return;

    const {
      id,
      name,
      imageUrl,
      groupIds,
      remark,
      orgId,
      areaId,
      bindingDevices,
    } = selectedResource;

    setEnvironmentDataWillAssign({
      id: id ?? '',
      name: name ?? '',
      imageUrl: imageUrl ?? '',
      groupIds: groupIds ?? [],
      remark: remark ?? undefined,
      orgId: orgId ?? '',
      areaId: areaId ?? '',
    });

    const isDasWBGT =
      bindingDevices?.some((d) => parseDasId(d) === 'daswbgt') ?? false;
    setEnvironmentPurpose(
      isDasWBGT && bindingDevices?.length > 0
        ? 'heatstroke-system'
        : 'confined-space',
    );
  }, [selectedResource]);

  const { data: assignedDasWBGT } = useQuery({
    queryKey: ['assigned-daswbgts', projectId, selectedResource?.id],
    queryFn: async () => {
      const dasWBGTs: DasWBGT[] = [];
      const bindingDevice = selectedResource?.bindingDevices;
      if (bindingDevice) {
        for (let i = 0; i < bindingDevice.length; i++) {
          const res = await getProjectDasWBGT({
            projectId: projectId as string,
            params: {
              dasId: bindingDevice[i],
              limit: 1,
            },
          });

          if (res.data.data.length === 1) {
            dasWBGTs.push(res.data.data[0]);
          }
        }
      }
      return dasWBGTs;
    },
    enabled: !!projectId && !!selectedResource,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (assignedDasWBGT.length > 0 && assignedDeviceFirstInit === false) {
      const init: Array<DasIdWillAssign> = assignedDasWBGT.map((device) => ({
        id: device.id,
        dasId: device.dasId,
        name: device.dasId,
        batteryLevel: 0,
        deviceType: 'daswbgt',
      }));

      setDeviceWillAssign(init);
      setInitDevices(init);
      setAssignedDeviceFirstInit(true);
    }
  }, [assignedDasWBGT, assignedDeviceFirstInit]);

  useEffect(() => {
    if (open) {
      setGroupsWillAssign(selectedGroups.map((group) => group.id));
    }
  }, [open, selectedGroups]);

  const handleEndpointChange = (
    dasId: string,
    update: DasWBGTEndpointUpdate,
  ) => {
    setWbgtEndpointUpdates((prev) => ({ ...prev, [dasId]: update }));
  };

  const saveable = () => {
    if (selectedResource && environmentDataWillAssign && resourceContacts) {
      if (Object.values(wbgtEndpointUpdates).some((u) => !u.isValid)) {
        return false;
      }
      if (
        !checkIsArrayEqual(selectedResource.groupIds, groupsWillAssign) ||
        !checkIsArrayEqual(initContact, listContact) ||
        checkIsAvatarUpdated(environmentDataWillAssign.avatarFile) ||
        checkIsFormEnvironmentEdited(
          selectedResource,
          environmentDataWillAssign,
        ) ||
        selectedResource.areaId !== environmentDataWillAssign.areaId ||
        !checkIsDasIdEqual(initDevices, deviceWillAssign) ||
        Object.values(wbgtEndpointUpdates).some((u) => u.isDirty)
      ) {
        return true;
      }
    }

    return false;
  };

  const handleEnvironmentDataWillAssign = (data: EnvironmentWillAssign) => {
    setEnvironmentDataWillAssign(data);
  };

  useEffect(() => {
    const updateChecked = checkedValue.map((item, index) =>
      index === navigatorIndex ? true : false,
    );
    setCheckedValue(updateChecked);
  }, [navigatorIndex]);

  const handleCloseEditDialog = () => {
    onCloseDialog();
    setEnvironmentDataWillAssign(undefined);
    setListContact([]);
    setGroupsWillAssign([]);
    setWbgtEndpointUpdates({});
    setDeviceWillAssign([]);
    setAssignedDeviceFirstInit(false);
    onSelectNavigatorIndex(1);
    setStatus('default');
    setMessage('');
  };

  const unbindContacts = async (contactIds: string[]) => {
    const requestUnbindContact = contactIds.map(async (contactId) => {
      const { data } = await mutateUnbindContact({
        projectId: projectId ?? '',
        contactId,
        referenceId: environmentDataWillAssign?.id ?? '',
      });
      return data;
    });
    await Promise.all(requestUnbindContact).catch((err) => {
      const error = err as Error;
      setMessage(error.message);
      setStatus('error');
      setSaveButtonStatus(undefined);
    });
    queryClient.invalidateQueries({
      queryKey: ['project-contacts', projectId as string],
    });
  };

  const bindContacts = async (contactIds: string[]) => {
    const requestBindContact = contactIds.map(async (contactId) => {
      const { data } = await mutateBindContact({
        projectId: projectId ?? '',
        contactId,
        referenceId: environmentDataWillAssign?.id ?? '',
        type: 'environment',
      });
      return data;
    });
    await Promise.all(requestBindContact).catch((err) => {
      const error = err as Error;
      setMessage(error.message);
      setStatus('error');
      setSaveButtonStatus(undefined);
    });
    queryClient.invalidateQueries({
      queryKey: ['project-contacts', projectId as string],
    });
  };

  const handleUpdoadAvatar = async (resourceId: string, file: File) => {
    await mutateUploadResourceAvatar({
      projectId: projectId ?? '',
      resourceType: 'environment',
      resourceId,
      imageFile: file,
    });
  };

  const handleUpdateResource = async (
    data: EnvironmentWillAssign,
    resourceId: string,
  ) => {
    const { name, orgId, areaId, remark } = data;

    const res = await mutateUpdateEnvironment(
      {
        projectId: projectId as string,
        resourceType: 'environment',
        resourceId,
        payload: {
          orgId,
          name,
          areaId,
          remark: remark ?? undefined,
        },
      },
      {
        onError: (error) => {
          if (error instanceof Error) {
            setMessage(error.message);
            setStatus('error');
          }
        },
      },
    );

    return res.data.data;
  };

  const updateGroups = async (resourceId?: string) => {
    if (!resourceId) {
      setMessage('No specific resourceId');
      setStatus('error');
    } else if (groupsWillAssign.length > 0) {
      await mutateSetResourceGroups({
        projectId: projectId as string,
        resourceType: 'environment',
        resourceId,
        payload: {
          groupIds: groupsWillAssign,
        },
      });
    } else {
      setMessage('Must select at least one group');
      setStatus('error');
    }
  };

  const handleSubmit = async () => {
    if (!environmentDataWillAssign) return;
    setSaveButtonStatus('loading');
    setStatus('default');
    try {
      await handleUpdateResource(
        environmentDataWillAssign,
        environmentDataWillAssign.id,
      );

      await updateGroups(selectedResource?.id);

      if (environmentDataWillAssign.avatarFile) {
        await handleUpdoadAvatar(
          environmentDataWillAssign.id,
          environmentDataWillAssign.avatarFile,
        );
      }

      // UNBIND CONTACT REQUEST
      const listContactUnbind = initContact.filter(
        (id) => !listContact.includes(id),
      );
      if (listContactUnbind.length > 0) {
        unbindContacts(listContactUnbind);
      }

      // BIND CONTACT REQUEST
      const listContactWillBind = listContact.filter(
        (id) => !initContact.includes(id),
      );
      if (listContactWillBind.length > 0) {
        bindContacts(listContactWillBind);
      }

      if (couldBindDevice && deviceWillAssign.length > 0) {
        await handleBindDevice(deviceWillAssign, environmentDataWillAssign.id);
      }

      const dasIdWillUnbind = initDevices.filter(
        (item) => !deviceWillAssign.some((dw) => dw.name === item.name),
      );

      if (couldBindDevice && dasIdWillUnbind.length > 0) {
        await handleUnbindDevice(dasIdWillUnbind, environmentDataWillAssign.id);
      }

      // SAVE DASWBGT ENDPOINT SETTINGS (name, coordinates, isDefaultWbgt)
      const endpointEntries = Object.entries(wbgtEndpointUpdates).filter(
        ([, u]) => u.isDirty,
      );
      if (endpointEntries.length > 0) {
        await Promise.all(
          endpointEntries.map(([, update]) =>
            updateEndPointFromProject({
              projectId: projectId as string,
              endpointId: update.endpointId,
              data: {
                name: update.name,
                coordinates: update.coordinates,
                isDefaultWbgt: update.isDefaultWbgt,
              },
            }),
          ),
        );
      }

      const onSuccsess = () => {
        queryClient.removeQueries({
          queryKey: ['assigned-daswbgts', projectId as string],
        });
        queryClient.removeQueries({
          queryKey: ['daswbgt-endpoint-card', projectId as string],
        });
        queryClient.invalidateQueries({
          queryKey: ['get-group-resources', projectId as string],
        });
        onSuccessCreate();

        onSuccessBind?.();
        setSaveButtonStatus('success');
      };

      let timer2: NodeJS.Timeout;
      const timer = setTimeout(() => {
        onSuccsess();
        handleCloseEditDialog();

        timer2 = setTimeout(() => {
          setSaveButtonStatus(undefined);
        }, 500);
      }, 500);
      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
      };
    } catch (err) {
      setSaveButtonStatus(undefined);
      const error = err as Error;
      setMessage(error.message);
      setStatus('error');
    }
  };

  let saveButtonStatusIcon;
  switch (saveButtonStatus) {
    case 'loading':
      saveButtonStatusIcon = <LoadingButtonSvgIcon />;
      break;
    case 'success':
      saveButtonStatusIcon = <CompleteButtonSvgIcon />;
      break;
    default:
      saveButtonStatusIcon = <SaveSvgIcon />;
  }

  useEffect(() => {
    setCheckedNavigator(
      checkedNavigator.map((_, index) =>
        index === navigatorIndex ? true : false,
      ),
    );
  }, [navigatorIndex]);

  const handleOnChangeArea = (areaId: string) => {
    setEnvironmentDataWillAssign({
      id: selectedResource?.id ?? '',
      name: selectedResource?.name ?? '',
      imageUrl: selectedResource?.imageUrl ?? '',
      groupIds: selectedResource?.groupIds ?? [],
      remark: selectedResource?.remark ?? undefined,
      orgId: selectedResource?.orgId ?? '',
      areaId,
    });
  };

  const handleOnChangeContact = (value: any) => {
    const contactId = value;
    const isContain = listContact.includes(contactId);
    if (!isContain) {
      setListContact([...listContact, contactId]);
    } else {
      setListContact(listContact.filter((item) => item !== contactId));
    }
  };

  const couldBindDevice =
    userProjectRole === 'owner' ||
    userProjectRole === 'admin' ||
    hasBindDevicePolicy;

  return (
    <ManagementDialog
      open={open}
      onClose={handleCloseEditDialog}
      status={status}
      message={message}
      title={title}
    >
      <DialogContent>
        <CheckList>
          {navigator.map((item, index) => {
            if (
              item.name === 'connected-device' &&
              environmentPurpose !== 'heatstroke-system'
            ) {
              return null;
            }
            let checked: boolean = false;
            switch (item.name) {
              case 'group':
                checked = true;
                break;
              case 'environment-basic-information':
                checked = checkEnvironmentBasicInformationAvailable(
                  environmentDataWillAssign,
                );
                break;
              case 'purpose-area':
                checked =
                  checkArea(environmentDataWillAssign?.areaId) &&
                  !!environmentPurpose;
                break;
              case 'emergency-contact':
                checked = checkContactAvailable(listContact);
                break;
              case 'connected-device':
                checked = hasAssignedDevice(deviceWillAssign);
                break;
            }
            return (
              <CheckListItem
                key={item.id}
                name={t(`project-settingJson.navigator.${item.name}`)}
                required={item.required}
                checked={checked}
                onClick={() => onSelectNavigatorIndex(index)}
                selected={checkedValue[index]}
                data-cy={`btn-list-navigator-environment-${item.name}`}
              />
            );
          })}
        </CheckList>
        <ContentWrapper className="content wrapper">
          {navigator[navigatorIndex]?.name === 'group' && (
            <GroupEdit
              groupIdsSelected={groupsWillAssign}
              handleGroupAssign={(groupIds) => setGroupsWillAssign(groupIds)}
              listGroup={filteredGroups}
              resource="environment"
            />
          )}
          {navigator[navigatorIndex]?.name ===
            'environment-basic-information' && (
            <EnvironmentBasicInformationContent
              data={environmentDataWillAssign}
              handleEnvironmentDataWillAssign={handleEnvironmentDataWillAssign}
            />
          )}
          {navigator[navigatorIndex]?.name === 'purpose-area' && (
            <PurposeAreaContent
              projectId={projectId}
              selectedAreaId={environmentDataWillAssign?.areaId}
              selectedPurpose={environmentPurpose}
              onAreaChange={handleOnChangeArea}
              onPurposeChange={(purpose) => setEnvironmentPurpose(purpose)}
            />
          )}
          {navigator[navigatorIndex]?.name === 'emergency-contact' && (
            <ContactEdit
              onChange={handleOnChangeContact}
              listContact={listContact}
            />
          )}
          {navigator[navigatorIndex]?.name === 'connected-device' &&
            environmentPurpose === 'heatstroke-system' && (
              <>
                {couldBindDevice ? (
                  <ConnectedDevice
                    projectId={projectId}
                    resourceType="environment"
                    data={deviceWillAssign}
                    handleDasIDWillAssign={(dasIds) =>
                      setDeviceWillAssign(dasIds)
                    }
                    onEndpointChange={handleEndpointChange}
                  />
                ) : (
                  <ManagementNoPermissionContent />
                )}
              </>
            )}
        </ContentWrapper>
      </DialogContent>
      <DialogActions>
        <DialogButton
          sx={{
            '&.Mui-disabled': {
              color: 'white',
            },
            '&:hover, &:active': {
              background: mainTheme.color.secondary.$60,
            },
            marginRight: 'auto',
          }}
          color="secondary"
          onClick={handleCloseEditDialog}
          data-cy="btn-cancel-edited-environment"
        >
          {t('cancel')}
        </DialogButton>
        <ConfirmButton
          sx={{
            '& .MuiButton-startIcon > svg': {
              width: '32px',
              height: '32px',
            },
          }}
          startIcon={saveButtonStatusIcon}
          status={saveButtonStatus}
          onClick={handleSubmit}
          disabled={!saveable()}
          data-cy="btn-save-edited-environment"
        >
          {t('save')}
        </ConfirmButton>
      </DialogActions>
    </ManagementDialog>
  );
};

export default EditEnvironment;
